/**
 * Permission Service
 * API client for permission and role management.
 * 
 * @author Enterprise User Management Module
 * @version 1.0.0
 */
import api from './api';

/**
 * Get all available permissions
 */
export const getPermissions = async () => {
  const response = await api.get('/api/v1/permissions');
  return response.data;
};

/**
 * Get permissions grouped by category
 */
export const getPermissionsByCategory = async () => {
  const response = await api.get('/api/v1/permissions/by-category');
  return response.data;
};

/**
 * Get the complete role-permission matrix
 */
export const getPermissionMatrix = async () => {
  const response = await api.get('/api/v1/permissions/matrix');
  return response.data;
};

/**
 * Get all roles with info
 */
export const getRoles = async () => {
  const response = await api.get('/api/v1/roles');
  return response.data;
};

/**
 * Get permissions for a specific role
 */
export const getRolePermissions = async (role) => {
  const response = await api.get(`/api/v1/roles/${role}/permissions`);
  return response.data;
};

/**
 * Update permissions for a role
 */
export const updateRolePermissions = async (role, permissions) => {
  const response = await api.put(`/api/v1/roles/${role}/permissions`, {
    permissions
  });
  return response.data;
};

/**
 * Reset a role to default permissions
 */
export const resetRolePermissions = async (role) => {
  const response = await api.post(`/api/v1/roles/${role}/reset`);
  return response.data;
};

/**
 * Reset all roles to default permissions
 */
export const resetAllPermissions = async () => {
  const response = await api.post('/api/v1/roles/reset-all');
  return response.data;
};

/**
 * Check if current user has a permission
 */
export const checkPermission = async (permission) => {
  const response = await api.get(`/api/v1/permissions/check/${permission}`);
  return response.data;
};

/**
 * Check multiple permissions at once
 */
export const checkPermissions = async (permissions) => {
  const response = await api.post('/api/v1/permissions/check', permissions);
  return response.data;
};

// Role color mapping for UI
export const ROLE_COLORS = {
  admin: 'red',
  designs_manager: 'purple',
  designs_engineer: 'blue',
  quality_manager: 'emerald',
  quality_engineer: 'teal',
  inventory_manager: 'amber',
  operator: 'orange',
  viewer: 'slate'
};

// Category display names
export const CATEGORY_LABELS = {
  users: 'User Management',
  roles: 'Role Management',
  settings: 'System Settings',
  audit: 'Audit & Compliance',
  parts: 'Parts Catalogue',
  taxonomy: 'Taxonomy',
  inventory: 'Inventory',
  quality: 'Quality Control',
  reports: 'Reports',
  system: 'System'
};

// Category icons (lucide-react icon names)
export const CATEGORY_ICONS = {
  users: 'Users',
  roles: 'Shield',
  settings: 'Settings',
  audit: 'FileText',
  parts: 'Package',
  taxonomy: 'FolderTree',
  inventory: 'Warehouse',
  quality: 'CheckCircle',
  reports: 'BarChart',
  system: 'Server'
};

export default {
  getPermissions,
  getPermissionsByCategory,
  getPermissionMatrix,
  getRoles,
  getRolePermissions,
  updateRolePermissions,
  resetRolePermissions,
  resetAllPermissions,
  checkPermission,
  checkPermissions,
  ROLE_COLORS,
  CATEGORY_LABELS,
  CATEGORY_ICONS
};
